import React, {Suspense, useCallback, useEffect, useMemo, useState} from 'react';
import { InventoryLabelLanding } from './InventoryLabelLanding';
import { InventoryLabelAssociateRoute } from './InventoryLabelAssociateRoute';
import { appNavigate } from '../utils/appNavigate';
import { appReplaceNavigate } from '../utils/appReplaceNavigate';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    Drawer,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Snackbar,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { getNavChromeColors } from '../theme/uiSemanticColors';
import BrandLogo from './design-system/BrandLogo';
import {
    MatrixDeploymentIcon,
    NodeFleetIcon,
    SatelliteHealthIcon,
} from '../branding/spatialNavIcons';
import { AppButton, AppIconButton, AppPage } from './design-system';
import {
    AccountCircle,
    Build,
    ChevronLeft,
    DarkMode,
    Edit,
    ExpandLess,
    ExpandMore,
    LightMode,
    Logout,
    Menu as MenuIcon,
    Notifications,
    Close,
    Person,
    Settings,
    // Additional improved icons
    Dashboard as DashboardIcon,
    Inventory2 as InventoryIcon,
    ViewInAr as ContainerIcon,
    Memory as WaferIcon,
    PersonAdd as CustomersIcon,
    ContactMail as ContactsIcon,
    Warning as ConflictIcon,
    InsertPhoto,
    Restore as HistoryIcon,
    TimelineOutlined as WaferHistoryIcon,
    FolderSpecial as ContainerHistoryIcon,
    Radar as TagCommandCenterIcon,
    Map as LocationOverviewIcon,
    SettingsInputAntenna as LocationServiceIcon,
    Layers as FloorPlanAdminIcon,
    Sensors as UwbStatusIcon,
    Dashboard as OperationsDashboardIcon,
    SupportAgent as SupportViewerIcon,
    Groups as UsersIcon,
    GroupWork as GroupsIcon,
    Payment as PaymentIcon,
    BarChart as AnalyticsIcon,
    ManageAccounts as SystemConfigIcon,
    // Provider-specific icons
    Apartment as TenantsIcon,
    Subscriptions as SubscriptionsIcon,
    LibraryBooks as TemplatesIcon,
    SettingsSuggest as SystemManagementIcon,
    DevicesOther as DeviceFleetIcon,
    Inventory2 as DeviceInventoryIcon,
    AssignmentOutlined as OrdersIcon,
    Router as GatewayHealthIcon,
    Security as GuardDashboardIcon,
    DevicesOther as EfemPeripheralsIcon,
    WarningAmber as UnattestedIcon,
    WorkHistory as WorkHistoryIcon,
    Drafts as DraftsIcon,
    Autorenew as WaferRefreshIcon,
    CameraAlt as WaferIdAcquireIcon,
    EventNote as AuditRunsIcon,
    HowToReg as DeviceClaimsIcon,
    SupervisorAccount as UserManagementIcon,
    DocumentScanner as ScanTemplatesIcon,
    Engineering as EfemSetupIcon,
    QrCodeScanner as QrCodeScannerIcon,
} from '@mui/icons-material';

import type {NavigationItem} from '../context/AppContext';
// Context hooks
import {useApp, useAuth, useNotifications, useTheme} from '../context/AppContext';
import { dismissEfemNotificationById } from '../utils/dismissedEfemJobsStorage';
import { unreadNotificationBadgeColor } from '../utils/notificationBadgeColor';
import { useSubdomain } from '../contexts/SubdomainContext';

// Amplify auth
import { signOut } from 'aws-amplify/auth';

// Performance utilities
import {memoryTracker} from '../utils/performance';

// Layout components
import { AboutSection } from './settings/AboutSection';

// Lazy-loaded components
import {
    TenantUtilization,
    ContainerTracking,
    CustomerManagement,
    ContactManagement,
    GroupManagementContainer,
    HistoryTracking,
    InventoryDashboard,
    TagCommandCenter,
    FloorPlanAdmin,
    ProviderLocationIntegrationAdmin,
    ProviderWaferIdModelTrainingPage,
    ProviderSiteAccessAdmin,
    LocationOverviewPage,
    TagFleetHealthPage,
    SiteAnchorHealthPage,
    UwbDeploymentPage,
    ProviderOperationsDashboard,
    UwbDeploymentStatusDashboard,
    ProviderSupportViewer,
    UserManagement,
    WaferManagement,
    ConflictManagement,
    WaferSlotUpdate,
    TempFilesPage,
    OperationsJobsPage,
    JobCreationWizardPage,
    ContainerSlotScanJobReviewPage,
    OperationsDraftsPage,
    ContainerCreateWorkflowPage,
    WaferDataRefreshWorkflowPage,
    WaferIdAcquirePage,
    AuditWorkflowPage,
    AuditRunsPage,
    LabelLinkMobileOnlyRoute,
    InsightFieldModeRouter,
} from './LazyComponents';
import { GlobalEfemLockIndicator } from './ui/GlobalEfemLockIndicator';
import WaferTransferRedirect from './WaferTransferRedirect';
import LocationLayout from './location/LocationLayout';
import AppShellToolbar from './AppShellToolbar';
import { AppToolbarProvider, useAppToolbarPageState, useAppToolbarOffset } from '../contexts/AppToolbarContext';
import { LocationProvider } from '../contexts/LocationContext';
import type { AppToolbarBreadcrumb } from '../utils/navigationBreadcrumbs';
import {
  buildNavigationBreadcrumbTrail,
  getRouteBreadcrumbSuffix,
  getStandaloneRouteBreadcrumb,
  navigationTrailToBreadcrumbs,
} from '../utils/navigationBreadcrumbs';
import { isLocationLayoutPath, LOCATION_LAYOUT_PATHS } from '../utils/appToolbarRoutes';
import { useMobileLayout } from '../hooks/useMobileLayout';
import { useMobileFieldViewportSync } from '../hooks/useMobileFieldViewportSync';
import { mobileContainersPath, isMobileFieldPath, resolveAppCleanPath, isMobileFieldUrl, isDesktopInventoryHomePath, canonicalizeAppPathname } from '../utils/mobileFieldPaths';
import { mobileFieldGroupedBg } from './mobile/mobileFieldTokens';

// Import Provider Dashboard
import ProviderDashboard from './ProviderDashboard';
import TenantManagement from './TenantManagement';
import ProviderUsers from './ProviderUsers';
import ProviderBillingAdmin from './provider/ProviderBillingAdmin';
import StripeProductManager from './provider/StripeProductManager';
import {ALL_USER_ROLES, isProviderRole} from "@cnapse/shared/data.types.js";
import { canShowNavigationItem } from '../utils/navigationAccess';
import { formatPersonName, personNameInitials } from '@cnapse/shared/person-name.utils.js';

// Import Billing Page (Stripe Customer Portal)
import BillingPage from '../pages/BillingPage';
import SubscriptionBlockedPage from '../pages/SubscriptionBlockedPage';
import RouteEntitlementBlocked from './common/RouteEntitlementBlocked';
import { resolveRouteCapabilityKey } from '../utils/routeEntitlement';

// Dialog components
import { EditProfileDialog } from './dialogs/EditProfileDialog';

// Settings components
import { MFASettings } from './settings/MFASettings';

// Import Provider Settings Pages
import SystemConfiguration from '../pages/ProviderSettings/SystemConfiguration';

// Import Device Fleet pages
import FleetDashboard from '../pages/DeviceFleet/FleetDashboard';
import DeviceInventory from '../pages/DeviceFleet/DeviceInventory';
import DeviceDetail from '../pages/DeviceFleet/DeviceDetail';
import Orders from '../pages/DeviceFleet/Orders';
import OrderDetail from '../pages/DeviceFleet/OrderDetail';
import GatewayHealthDashboard from '../pages/DeviceFleet/GatewayHealthDashboard';
import GuardDashboard from '../pages/DeviceFleet/GuardDashboard';
import EfemPeripheralsDashboard from '../pages/DeviceFleet/EfemPeripheralsDashboard';
import SiteAnchorHealthDashboard from '../pages/DeviceFleet/SiteAnchorHealthDashboard';
import SiteTagFleetDashboard from '../pages/DeviceFleet/SiteTagFleetDashboard';
import UnattestedDashboard from '../pages/DeviceFleet/UnattestedDashboard';
import DeviceClaims from '../pages/Tenant/DeviceClaims';
import TenantDeviceDetail from '../pages/Tenant/TenantDeviceDetail';
import EfemSetupPage from '../pages/Tenant/EfemSetupPage';
import ScanTemplatesPage from '../pages/Tenant/ScanTemplatesPage';
import ProviderEfemSetupPage from '../pages/Provider/ProviderEfemSetupPage';

// Import auth dialogs
import ChangePasswordDialog from './auth/ChangePasswordDialog';

// Import hooks
import { useAppConfig } from '../hooks/useAppConfig';

// Import Formatting Context
import { FormattingProvider, useFormatting } from '../contexts/FormattingContext';

// Import Tenant Features Context
import { TenantFeaturesProvider, useTenantFeatures } from '../contexts/TenantFeaturesContext';
import { useAppToolbar } from '../contexts/AppToolbarContext';

// Placeholder component for missing components
const PlaceholderComponent: React.FC<{ title: string; currentUserRole: ALL_USER_ROLES }> = ({ title, currentUserRole }) => {
  const { user } = useAuth();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { actions } = useApp();

  const handleProfileUpdated = async (updatedUser: {
    firstName: string;
    lastName: string;
    phone?: string;
    avatarUrl?: string;
  }) => {
    try {
      console.log('Updating user profile in context with:', updatedUser);

      // Update AppContext with the updated profile data (without login message)
      if (user) {
        actions.updateUserProfile({
          name: `${updatedUser.firstName} ${updatedUser.lastName}`,
          firstName: updatedUser.firstName,
          lastName: updatedUser.lastName,
          phone: updatedUser.phone,
          avatar: updatedUser.avatarUrl,
        });

        // Show profile update success message
        actions.showSnackbar('Profile updated successfully', 'success');

        console.log('✅ Profile updated successfully in context');
      }
    } catch (error) {
      console.error('❌ Failed to update profile in context:', error);
    }
  };

  const profileFirstName = (user as { firstName?: string } | null)?.firstName;
  const profileLastName = (user as { lastName?: string } | null)?.lastName;
  const profilePhone = (user as { phone?: string } | null)?.phone;
  const profileAvatarUrl = user?.avatar;
  const profileDisplayName = formatPersonName(profileFirstName, profileLastName) || user?.name || 'User';

  const profileToolbarActions = useMemo(
    () =>
      title === 'User Profile' ? (
        <AppButton variant="outlined" startIcon={<Edit />} onClick={() => setEditDialogOpen(true)}>
          Edit Profile
        </AppButton>
      ) : null,
    [title],
  );

  useAppToolbar({ pageActions: profileToolbarActions });

  // Enhanced content for Profile and Settings pages
  if (title === 'User Profile') {
    return (
      <AppPage>
        <Card sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack spacing={3}>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 3,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Avatar
                  src={profileAvatarUrl}
                  sx={(theme) => ({
                    width: 112,
                    height: 112,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: theme.typography.h4.fontSize,
                    fontWeight: 600,
                    border: `3px solid ${theme.palette.background.paper}`,
                    boxShadow: theme.shadows[4],
                  })}
                >
                  {personNameInitials(profileFirstName, profileLastName)}
                </Avatar>
                <Typography variant="h5" fontWeight={600}>
                  {profileDisplayName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {user?.email}
                </Typography>
                {user?.role ? (
                  <Chip
                    label={user.role}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 1.5 }}
                  />
                ) : null}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>Profile Information</Typography>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">First Name</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {profileFirstName || 'Not specified'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Last Name</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {profileLastName || 'Not specified'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" fontWeight={500}>{user?.email}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Phone Number</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {profilePhone || 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">Role</Typography>
                    <Typography variant="body1" fontWeight={500}>{user?.role}</Typography>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        
        {/* Edit Profile Dialog */}
        {user && (
          <EditProfileDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            currentUser={{
              firstName: (user as { firstName?: string })?.firstName || '',
              lastName: (user as { lastName?: string })?.lastName || '',
              phone: (user as { phone?: string })?.phone,
              avatarUrl: user?.avatar,
            }}
            onSuccess={handleProfileUpdated}
            onAvatarChange={(avatarUrl) => {
              actions.updateUserProfile({ avatar: avatarUrl });
            }}
          />
        )}
      </AppPage>
    );
  }

  if (title === 'User Settings') {
    return (
      <AppPage>
        <Stack spacing={3} sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}>
          {/* Security Section */}
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Security
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>Password</Typography>
                  <AppButton variant="outlined" onClick={() => setChangePasswordOpen(true)}>
                    Change Password
                  </AppButton>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* MFA Section */}
          <Card>
            <CardContent>
              <MFASettings />
            </CardContent>
          </Card>

          {/* Preferences Section */}
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Preferences
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Theme</Typography>
                  <Typography variant="body1">{user?.preferences?.theme || 'System default'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Language</Typography>
                  <Typography variant="body1">{user?.preferences?.language || 'English'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Notifications</Typography>
                  <Typography variant="body1">{user?.preferences?.notifications ? 'Enabled' : 'Disabled'}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <AboutSection />
            </CardContent>
          </Card>
        </Stack>
        
        {/* Change Password Dialog */}
        <ChangePasswordDialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
          onSuccess={() => {
            // Optional: Show a success message or log the user out
            console.log('Password changed successfully');
          }}
        />
      </AppPage>
    );
  }

  return (
    <AppPage sx={{ textAlign: 'center', alignItems: 'center' }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Welcome to the {title} section.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Current User Role: {currentUserRole}
      </Typography>
      <Typography variant="body2" color="primary">
        This component is currently under development and will be available soon.
      </Typography>
    </AppPage>
  );
};

/**
 * Inner component that uses formatting context
 */
const AppToolbarOffsetSpacer: React.FC = () => {
  const toolbarOffset = useAppToolbarOffset();
  return <Box sx={{ height: toolbarOffset, flexShrink: 0 }} />;
};

const InsightAppInner: React.FC = React.memo(() => {
  // Context hooks
  const { state, dispatch } = useApp();
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, remove: removeNotification } = useNotifications();
  const notificationBadgeColor = useMemo(
    () => unreadNotificationBadgeColor(notifications),
    [notifications],
  );
  const { theme, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const navChrome = useMemo(() => getNavChromeColors(muiTheme), [muiTheme]);
  const appConfig = useAppConfig();
  const { formatTime } = useFormatting();
  const { subdomain, isActive, isLoading: subscriptionLoading, error: subscriptionError } = useSubdomain();
  const { isRouteEnabled, isLoading: featuresLoading } = useTenantFeatures();

  // Local state for UI elements
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Persist expanded menus in localStorage with all parent menus expanded by default
  const [expandedMenus, setExpandedMenus] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('cnapse-insight-expanded-menus');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load expanded menus from localStorage:', error);
    }
    // Default: expand all parent menus for better UX
    return [
      'history',
      'user-management',
      'customers-billing',
      'platform-admin',
      'deploy-configure',
      'monitor-health',
      'inventory-orders',
    ];
  });
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [popupMenuAnchor, setPopupMenuAnchor] = useState<null | HTMLElement>(null);
  const [popupMenuItem, setPopupMenuItem] = useState<NavigationItem | null>(null);
  const popupTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Track current path for client-side navigation (without page reload)
  const [currentPath, setCurrentPath] = useState<string>(() => window.location.pathname);
  
  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Performance monitoring
  React.useEffect(() => {
    const cleanup = memoryTracker.trackMemoryLeak('InsightApp');
    return cleanup;
  }, []);

  // Check if user is a provider (calculate before early return)
  const isProviderUser = user ? isProviderRole(user.role) : false;
  const isMobileLayout = useMobileLayout();

  // Navigation configuration - memoized for performance (must be before early return)
  const navigationItems: NavigationItem[] = useMemo(() => {
    const baseItems: NavigationItem[] = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/',
        roles: ['Admin', 'Lead', 'Engineer'],
      }
    ];

    // Add provider-specific navigation items (workflow-based grouping)
    if (isProviderUser) {
      if (user?.role === ALL_USER_ROLES.provider_support) {
        return [
          ...baseItems.map((item) => ({ ...item, roles: ['Support'] as NavigationItem['roles'] })),
          {
            id: 'provider-support-workspace',
            label: 'Customer support',
            icon: <SupportViewerIcon />,
            roles: ['Support'],
            children: [
              {
                id: 'support-tenants',
                label: 'Tenants',
                icon: <TenantsIcon />,
                path: '/tenants',
                roles: ['Support'],
              },
              {
                id: 'provider-location-support',
                label: 'Support Viewer',
                icon: <SupportViewerIcon />,
                path: '/provider/location-support',
                roles: ['Support'],
              },
            ],
          },
        ];
      }

      return [
        ...baseItems,
        {
          id: 'customers-billing',
          label: 'Customers & billing',
          icon: <PaymentIcon />,
          roles: ['Admin', 'Lead', 'Engineer'],
          children: [
            {
              id: 'tenants',
              label: 'Tenants',
              icon: <TenantsIcon />,
              path: '/tenants',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'tenant-subscriptions',
              label: 'Tenant Subscriptions',
              icon: <SubscriptionsIcon />,
              path: '/tenant-subscriptions',
              roles: ['Admin', 'Lead'],
            },
            {
              id: 'subscription-plans',
              label: 'Products & Pricing',
              icon: <TemplatesIcon />,
              path: '/subscription-plans',
              roles: ['Admin'],
            },
            {
              id: 'analytics',
              label: 'Tenant Utilization',
              icon: <AnalyticsIcon />,
              path: '/analytics',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
          ],
        },
        {
          id: 'platform-admin',
          label: 'Platform admin',
          icon: <SystemManagementIcon />,
          roles: ['Admin'],
          children: [
            {
              id: 'provider-users',
              label: 'Provider Users',
              icon: <UsersIcon />,
              path: '/provider/users',
              roles: ['Admin'],
            },
            {
              id: 'system-config',
              label: 'System Configuration',
              icon: <SystemConfigIcon />,
              path: '/system/config',
              roles: ['Admin'],
            },
          ],
        },
        {
          id: 'deploy-configure',
          label: 'Deploy & configure',
          icon: <EfemSetupIcon />,
          roles: ['Admin', 'Lead', 'Engineer', 'Deployment'],
          children: [
            {
              id: 'provider-location-deployment',
              label: 'Matrix Deployment',
              icon: <MatrixDeploymentIcon />,
              path: '/provider/location-deployment',
              roles: ['Admin', 'Engineer', 'Deployment'],
            },
            {
              id: 'gateway-setup',
              label: 'Axon Setup',
              icon: <EfemSetupIcon />,
              path: '/provider/efem-setup',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'provider-location-integration',
              label: 'Spatial Integration',
              icon: <LocationOverviewIcon />,
              path: '/provider/location-integration',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'provider-wafer-id-ml',
              label: 'CCI Vision Model Training',
              icon: <AnalyticsIcon />,
              path: '/provider/vision/model-training',
              roles: ['Admin', 'Engineer'],
            },
            {
              id: 'provider-site-access',
              label: 'Site Access',
              icon: <DeviceClaimsIcon />,
              path: '/provider/site-access',
              roles: ['Admin'],
            },
          ],
        },
        {
          id: 'monitor-health',
          label: 'Monitor & health',
          icon: <DeviceFleetIcon />,
          roles: ['Admin', 'Lead', 'Engineer'],
          children: [
            {
              id: 'fleet-dashboard',
              label: 'Fleet Dashboard',
              icon: <DeviceFleetIcon />,
              path: '/provider/devices',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'gateway-health-dashboard',
              label: 'Edge Health',
              icon: <GatewayHealthIcon />,
              path: '/provider/ops/gateway-health',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'guard-dashboard',
              label: 'Guard Dashboard',
              icon: <GuardDashboardIcon />,
              path: '/provider/ops/guard-dashboard',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'site-anchor-health-dashboard',
              label: 'Satellite Health',
              icon: <SatelliteHealthIcon />,
              path: '/provider/ops/site-anchor-health',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'site-tag-fleet-dashboard',
              label: 'Node Fleet',
              icon: <NodeFleetIcon />,
              path: '/provider/ops/site-tag-fleet',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'efem-peripherals-dashboard',
              label: 'Axon Peripherals',
              icon: <EfemPeripheralsIcon />,
              path: '/provider/ops/efem-peripherals',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'unattested-dashboard',
              label: 'Unattested Devices',
              icon: <UnattestedIcon />,
              path: '/provider/ops/unattested',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'provider-location-operations',
              label: 'Spatial Status',
              icon: <OperationsDashboardIcon />,
              path: '/provider/location-operations',
              roles: ['Admin'],
            },
            {
              id: 'provider-uwb-status',
              label: 'Matrix Status',
              icon: <UwbStatusIcon />,
              path: '/provider/location-uwb-status',
              roles: ['Admin'],
            },
          ],
        },
        {
          id: 'inventory-orders',
          label: 'Inventory & orders',
          icon: <DeviceInventoryIcon />,
          roles: ['Admin', 'Lead', 'Engineer', 'Deployment'],
          children: [
            {
              id: 'device-inventory',
              label: 'Device Inventory',
              icon: <DeviceInventoryIcon />,
              path: '/provider/devices/inventory',
              roles: ['Admin', 'Lead', 'Engineer'],
            },
            {
              id: 'orders',
              label: 'Orders',
              icon: <OrdersIcon />,
              path: '/provider/orders',
              roles: ['Admin', 'Lead', 'Engineer', 'Deployment'],
            },
          ],
        },
        {
          id: 'provider-location-support',
          label: 'Support Viewer',
          icon: <SupportViewerIcon />,
          path: '/provider/location-support',
          roles: ['Admin', 'Support'],
        },
      ];
    }

    // Tenant navigation items
    return [
      ...baseItems,
      {
        id: 'inventory',
        label: 'Inventory Management',
        icon: <InventoryIcon />,
        roles: ['Admin', 'Lead', 'Engineer'],
        children: [
          {
            id: 'containers',
            label: 'Containers',
            icon: <ContainerIcon />,
            path: '/containers',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
          {
            id: 'wafers',
            label: 'Wafers',
            icon: <WaferIcon />,
            path: '/wafers',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
          {
            id: 'link-label-handheld',
            label: 'Scan QR',
            icon: <QrCodeScannerIcon />,
            path: '/labels/link/handheld',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
        ],
      },
      {
        id: 'operations',
        label: 'Operations',
        icon: <Build />,
        roles: ['Admin', 'Lead', 'Engineer'],
        children: [
          {
            id: 'ops-jobs',
            label: 'Jobs',
            icon: <WorkHistoryIcon />,
            path: '/operations/jobs',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
          {
            id: 'ops-drafts',
            label: 'My Drafts',
            icon: <DraftsIcon />,
            path: '/operations/drafts',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
          {
            id: 'ops-wafer-id-acquire',
            label: 'Wafer ID Acquire',
            icon: <WaferIdAcquireIcon />,
            path: '/operations/wafer-id-acquire',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'waferIdAcquire',
            disabledTooltip: 'CCI Vision not available in your plan',
          },
          {
            id: 'ops-wafer-refresh',
            label: 'Wafer Data Refresh',
            icon: <WaferRefreshIcon />,
            path: '/operations/wafer-data-refresh',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'waferDataRefresh',
            disabledTooltip: 'CCI Vision not available in your plan',
          },
          {
            id: 'ops-audit-runs',
            label: 'Audits',
            icon: <AuditRunsIcon />,
            path: '/operations/audit-runs',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'audits',
            disabledTooltip: 'CCI Vision not available in your plan',
          },
          {
            id: 'ops-conflicts',
            label: 'Conflict Resolution',
            icon: <ConflictIcon />,
            path: '/operations/conflicts',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'conflictResolution',
            disabledTooltip: 'CCI Vision not available in your plan',
          },
          {
            id: 'ops-temp-files',
            label: 'Temp files',
            icon: <InsertPhoto />,
            path: '/operations/temp-files',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
        ],
      },
      {
        id: 'history',
        label: 'Asset History',
        icon: <HistoryIcon />,
        roles: ['Admin', 'Lead', 'Engineer'],
        children: [
          {
            id: 'wafer-history',
            label: 'Wafer History',
            icon: <WaferHistoryIcon />,
            path: '/history/wafers',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
          {
            id: 'container-history',
            label: 'Container History',
            icon: <ContainerHistoryIcon />,
            path: '/history/containers',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
        ],
      },
      {
        id: 'spatial-service',
        label: 'CCI Spatial',
        icon: <LocationServiceIcon />,
        roles: ['Admin', 'Lead', 'Engineer'],
        children: [
          {
            id: 'location-tracking',
            label: 'Node Command Center',
            icon: <TagCommandCenterIcon />,
            path: '/location',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'nodeCommandCenter',
            disabledTooltip: 'CCI Spatial not available in your plan',
          },
          {
            id: 'location-overview',
            label: 'Spatial Overview',
            icon: <LocationOverviewIcon />,
            path: '/location/overview',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'spatialOverview',
            disabledTooltip: 'CCI Spatial not available in your plan',
          },
          {
            id: 'location-tag-fleet',
            label: 'Node Fleet Health',
            icon: <NodeFleetIcon />,
            path: '/location/tag-fleet',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'nodeFleetHealth',
            disabledTooltip: 'CCI Spatial not available in your plan',
          },
          {
            id: 'location-anchor-health',
            label: 'Satellite Health',
            icon: <SatelliteHealthIcon />,
            path: '/location/anchor-health',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'satelliteHealth',
            disabledTooltip: 'CCI Spatial (UWB) not available in your plan',
          },
          {
            id: 'location-floor-plans',
            label: 'Cleanroom Layout',
            icon: <FloorPlanAdminIcon />,
            path: '/admin/location/floor-plans',
            roles: ['Admin'],
            requiredRoute: 'cleanroomLayout',
            disabledTooltip: 'CCI Spatial (UWB) not available in your plan',
          },
        ],
      },
      {
        id: 'device-claims',
        label: 'Setup',
        icon: <Build />,
        roles: ['Admin', 'Lead', 'Engineer'],
        children: [
          {
            id: 'device-claims-list',
            label: 'Devices',
            icon: <DeviceClaimsIcon />,
            path: '/devices/claims',
            roles: ['Admin', 'Lead', 'Engineer'],
          },
          {
            id: 'efem-setup',
            label: 'Axon Setup',
            icon: <EfemSetupIcon />,
            path: '/devices/efem-setup',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'axonSetup',
            disabledTooltip: 'Axon Gateway licenses not included in your plan',
          },
          {
            id: 'scan-templates',
            label: 'Scan Templates',
            icon: <ScanTemplatesIcon />,
            path: '/devices/scan-templates',
            roles: ['Admin', 'Lead', 'Engineer'],
            requiredRoute: 'scanTemplates',
            disabledTooltip: 'CCI Vision not available in your plan',
          },
        ],
      },
      {
        id: 'user-management',
        label: 'User Management',
        icon: <UserManagementIcon />,
        roles: ['Admin', 'Lead'],
        children: [
          {
            id: 'users',
            label: 'Users',
            icon: <Person />,
            path: '/users',
            roles: ['Admin', 'Lead'],
          },
          {
            id: 'groups',
            label: 'Groups',
            icon: <GroupsIcon />,
            path: '/groups',
            roles: ['Admin'],
          },
          {
            id: 'contacts',
            label: 'Contacts',
            icon: <ContactsIcon />,
            path: '/contacts',
            roles: ['Admin', 'Lead'],
          },
          {
            id: 'customers',
            label: 'Customers',
            icon: <CustomersIcon />,
            path: '/customers',
            roles: ['Admin', 'Lead'],
          },
        ],
      },
      {
        id: 'billing',
        label: 'Billing & Subscription',
        icon: <PaymentIcon />,
        path: '/billing',
        roles: ['Admin'],
      },
    ];
  }, [isProviderUser, user]);

  // Helper functions optimized with useCallback (must be before early return)
  const hasPermission = useCallback((item: NavigationItem): boolean => {
    if (!user) return false;
    return canShowNavigationItem(user.role, item, isRouteEnabled);
  }, [user, isRouteEnabled]);

  const handleMenuToggle = useCallback((menuId: string) => {
    setExpandedMenus(prev => {
      const newExpandedMenus = prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId];
      
      // Persist to localStorage
      try {
        localStorage.setItem('cnapse-insight-expanded-menus', JSON.stringify(newExpandedMenus));
      } catch (error) {
        console.error('Failed to save expanded menus to localStorage:', error);
      }
      
      return newExpandedMenus;
    });
  }, []);

  const handleDrawerToggle = useCallback(() => {
    dispatch({ type: 'TOGGLE_DRAWER' });
  }, [dispatch]);

  const handleUserMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleProfileClick = useCallback(() => {
    // Navigate to profile page (client-side navigation, no reload)
    const fullPath = subdomain ? `/${subdomain}/profile` : '/profile';
    window.history.pushState({}, '', fullPath);
    setCurrentPath(fullPath);
    handleUserMenuClose();
  }, [handleUserMenuClose, subdomain]);

  const handleSettingsClick = useCallback(() => {
    // Navigate to settings page (client-side navigation, no reload)
    const fullPath = subdomain ? `/${subdomain}/settings` : '/settings';
    window.history.pushState({}, '', fullPath);
    setCurrentPath(fullPath);
    handleUserMenuClose();
  }, [handleUserMenuClose, subdomain]);

  const handleNotificationClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  }, []);

  const handleNotificationClose = useCallback(() => {
    setNotificationAnchor(null);
  }, []);

  const handleDismissNotification = useCallback(
    (notificationId: string) => {
      dismissEfemNotificationById(notificationId);
      removeNotification(notificationId);
    },
    [removeNotification],
  );

  const handlePopupMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, item: NavigationItem) => {
    if (!state.drawerOpen && item.children && item.children.length > 0) {
      // Clear any existing timeout
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
        popupTimeoutRef.current = null;
      }
      setPopupMenuAnchor(event.currentTarget);
      setPopupMenuItem(item);
    }
  }, [state.drawerOpen]);

  const handlePopupMenuClose = useCallback(() => {
    // Add a small delay before closing to allow mouse movement to popup
    popupTimeoutRef.current = setTimeout(() => {
      setPopupMenuAnchor(null);
      setPopupMenuItem(null);
    }, 100);
  }, []);

  const handlePopupMenuEnter = useCallback(() => {
    // Cancel the close timeout when mouse enters popup
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
  }, []);

  const handlePopupMenuLeave = useCallback(() => {
    // Close immediately when leaving popup
    setPopupMenuAnchor(null);
    setPopupMenuItem(null);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      // Sign out from Cognito - this handles everything including token revocation
      await signOut();
      
      // Clear the user from context (this will trigger AuthWrapper to show login)
      dispatch({ type: 'LOGOUT' });
      
      // Show success message
      dispatch({ 
        type: 'SHOW_SNACKBAR', 
        payload: { message: 'Logged out successfully', severity: 'info' }
      });
      
      handleUserMenuClose();
      
      // Force a full page reload to clear all state and redirect to login
      // This ensures clean navigation state for the next login
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    } catch (error) {
      console.error('Logout error:', error);
      // Even if sign out fails, clear local state
      dispatch({ type: 'LOGOUT' });
      dispatch({ 
        type: 'SHOW_SNACKBAR', 
        payload: { message: 'Logged out locally', severity: 'warning' }
      });
      handleUserMenuClose();
      
      // Force reload even on error
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    }
  }, [dispatch, handleUserMenuClose]);

  // Calculate clean path (without subdomain) for route matching
  const path = currentPath;
  const cleanPath = resolveAppCleanPath(path, subdomain);
  const isMobileFieldBootstrap = isMobileFieldUrl(path, subdomain);

  const mobileFieldDesktopEscapePending = useMobileFieldViewportSync(
    cleanPath,
    subdomain,
    Boolean(user) && !isProviderUser,
  );

  useEffect(() => {
    const canonical = canonicalizeAppPathname(path, subdomain);
    if (canonical) {
      appReplaceNavigate(canonical);
    }
  }, [path, subdomain]);

  useEffect(() => {
    if (!user || isProviderUser || !isMobileLayout) {
      return;
    }
    if (isMobileFieldUrl(path, subdomain)) {
      return;
    }
    if (isDesktopInventoryHomePath(cleanPath)) {
      appReplaceNavigate(mobileContainersPath(subdomain));
    }
  }, [user, isProviderUser, isMobileLayout, cleanPath, subdomain, path]);

  const pageState = useAppToolbarPageState();

  const appBreadcrumbs = useMemo(() => {
    const homeHref = subdomain ? `/${subdomain}/` : '/';
    const pathPrefix = subdomain ? `/${subdomain}` : '';
    const crumbs: AppToolbarBreadcrumb[] = [{ label: 'Home', href: homeHref }];

    const trail = buildNavigationBreadcrumbTrail(navigationItems, cleanPath);
    if (trail) {
      crumbs.push(...navigationTrailToBreadcrumbs(trail, pathPrefix));
    } else {
      const standalone = getStandaloneRouteBreadcrumb(cleanPath);
      if (standalone) {
        crumbs.push(standalone);
      }
    }

    const suffix = getRouteBreadcrumbSuffix(cleanPath);
    if (suffix.length > 0) {
      crumbs.push(
        ...suffix.map((crumb) =>
          crumb.href ? { ...crumb, href: `${pathPrefix}${crumb.href}` } : crumb,
        ),
      );
    }

    if (pageState.breadcrumbSuffix.length > 0) {
      crumbs.push(...pageState.breadcrumbSuffix);
    }

    return crumbs;
  }, [cleanPath, navigationItems, pageState.breadcrumbSuffix, subdomain]);

  // Render navigation items - memoized for performance
  const renderNavigationItem = useCallback((item: NavigationItem, level = 0) => {
    if (!hasPermission(item)) return null;

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.id);
    const isActive = !!(item.path && cleanPath === item.path);

    return (
      <React.Fragment key={item.id}>
        <ListItem 
          disablePadding 
          sx={{ 
            display: 'block',
            mb: level === 0 ? 0.5 : 0,
          }}
        >
          <ListItemButton
            selected={isActive}
            onMouseEnter={(e) => hasChildren && !state.drawerOpen && handlePopupMenuOpen(e, item)}
            onMouseLeave={() => hasChildren && !state.drawerOpen && handlePopupMenuClose()}
            sx={{
              minHeight: 48,
              justifyContent: state.drawerOpen ? 'initial' : 'center',
              px: state.drawerOpen ? 2.5 : 0,
              pl: state.drawerOpen ? (level > 0 ? 4.5 : 2.5) : 0,
              py: level > 0 ? 1 : 1.25,
              borderRadius: level > 0 ? 0 : '8px',
              mx: state.drawerOpen ? (level > 0 ? 0 : 1) : 0,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: level > 0 ? 'action.hover' : navChrome.itemHoverBg,
                transform: state.drawerOpen && level === 0 ? 'translateX(4px)' : 'none',
              },
              '&.Mui-selected': {
                backgroundColor: navChrome.itemSelectedBg,
                borderLeft: level > 0 ? '3px solid' : 'none',
                borderLeftColor: 'primary.main',
                '&:hover': {
                  backgroundColor: navChrome.itemSelectedHoverBg,
                },
              },
            }}
            onClick={() => {
              if (hasChildren) {
                handleMenuToggle(item.id);
              } else if (item.path) {
                // Include subdomain for tenant users
                const fullPath = subdomain ? `/${subdomain}${item.path}` : item.path;
                // Use history API for client-side navigation without page reload
                window.history.pushState({}, '', fullPath);
                setCurrentPath(fullPath);
              }
            }}
          >
            <ListItemIcon 
              sx={{ 
                minWidth: 0, 
                mr: state.drawerOpen ? 2.5 : 0,
                color: isActive ? 'primary.main' : 'inherit',
                justifyContent: 'center',
              }}
            >
              {item.badge && item.badge > 0 ? (
                <Badge 
                  badgeContent={item.badge} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.65rem',
                      height: '16px',
                      minWidth: '16px',
                    }
                  }}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText 
              primary={item.label}
              primaryTypographyProps={{
                fontSize: level > 0 ? '0.875rem' : '0.9375rem',
                fontWeight: isActive ? 600 : level > 0 ? 400 : 500,
                color: isActive ? 'primary.main' : 'inherit',
              }}
              sx={{ 
                display: state.drawerOpen ? 'block' : 'none',
                m: 0,
              }}
            />
            {hasChildren && (
              <Box 
                sx={{ 
                  display: state.drawerOpen ? 'flex' : 'none',
                  alignItems: 'center',
                  transition: 'transform 0.2s ease-in-out',
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(0deg)',
                }}
              >
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </Box>
            )}
          </ListItemButton>
        </ListItem>
        
        {hasChildren && (
          <Collapse in={isExpanded && state.drawerOpen} timeout="auto" unmountOnExit>
            <List 
              component="div" 
              disablePadding
              sx={{
                backgroundColor: navChrome.nestedListBg,
                ...(navChrome.nestedListBorder !== 'transparent' && {
                  borderLeft: '2px solid',
                  borderLeftColor: navChrome.nestedListBorder,
                }),
                ml: 3.5,
                my: 0.5,
              }}
            >
              {item.children?.map(child => renderNavigationItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  }, [hasPermission, expandedMenus, handleMenuToggle, state.drawerOpen, cleanPath, subdomain, navChrome, setCurrentPath, handlePopupMenuClose, handlePopupMenuOpen]);

  const drawerWidth = 280;
  const drawerOpenPaddingPx = 20; // Matches sx px: 2.5 on drawer header/nav items.
  const topLevelNavOuterMarginPx = 8; // Matches sx mx: 1 for top-level nav rows.
  const topLevelIconAndGapPx = 44; // 24px icon + 20px icon/text gap (mr: 2.5).
  const fullLogoIntrinsicLeftWhitespacePx = 8; // Compensation for whitespace in full logo SVG.
  const fullLogoOpenOffsetPx =
    topLevelNavOuterMarginPx + topLevelIconAndGapPx - fullLogoIntrinsicLeftWhitespacePx;

  // Memoized route content for performance optimization
  const routeContent = useMemo(() => {
    // Return loading spinner if user not ready
    if (!user) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }

    // Check subscription status for tenant users (skip for provider users)
    // Show spinner while subscription data is loading
    if (!isProviderUser && subscriptionLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }

    // Billing remains reachable when subscription is inactive (matches backend billing routes).
    const isBillingRoute = cleanPath === '/billing';

    // Only show blocked page after loading completes and subscription is inactive
    if (!isProviderUser && !isActive && !isBillingRoute) {
      // Show blocked page for tenant users without active subscription
      return <SubscriptionBlockedPage message={subscriptionError || undefined} />;
    }

    if (!isProviderUser && featuresLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }
    
    // If provider user, handle provider-specific routes
    if (isProviderUser) {
      const providerHome =
        user.role === ALL_USER_ROLES.provider_deployment_engineer
          ? { title: 'Matrix Deployment', component: UwbDeploymentPage }
          : user.role === ALL_USER_ROLES.provider_support
            ? { title: 'Tenants', component: () => <TenantManagement readOnly /> }
          : { title: 'Provider Dashboard', component: ProviderDashboard };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const providerRoutes: { [key: string]: { title: string; component?: React.ComponentType<any> } } = {
        '/': providerHome,
        '/provider/users': { title: 'Provider User Management', component: ProviderUsers },
        '/tenants': {
          title: user.role === ALL_USER_ROLES.provider_support ? 'Tenants (read-only)' : 'Tenant Management',
          component: user.role === ALL_USER_ROLES.provider_support
            ? () => <TenantManagement readOnly />
            : TenantManagement,
        },
        '/tenant-subscriptions': { title: 'Tenant Subscriptions', component: ProviderBillingAdmin },
        '/subscription-plans': { title: 'Products & Pricing', component: StripeProductManager },
        '/provider/system-configuration': { title: 'System Configuration', component: SystemConfiguration },
        '/system/config': { title: 'System Configuration', component: SystemConfiguration },        
        '/analytics': { title: 'Tenant Utilization', component: TenantUtilization },
        '/provider/devices': { title: 'Fleet Dashboard', component: FleetDashboard },
        '/provider/devices/inventory': { title: 'Device Inventory', component: DeviceInventory },
        '/provider/orders': { title: 'Orders', component: Orders },
        '/provider/ops/gateway-health': { title: 'Edge Health', component: GatewayHealthDashboard },
        '/provider/ops/guard-dashboard': { title: 'Guard Dashboard', component: GuardDashboard },
        '/provider/ops/unattested': { title: 'Unattested Devices', component: UnattestedDashboard },
        '/provider/ops/efem-peripherals': { title: 'Axon Peripherals', component: EfemPeripheralsDashboard },
        '/provider/ops/site-anchor-health': { title: 'Satellite Health', component: SiteAnchorHealthDashboard },
        '/provider/ops/site-tag-fleet': { title: 'Node Fleet', component: SiteTagFleetDashboard },
        '/provider/location-integration': { title: 'Spatial Integration', component: ProviderLocationIntegrationAdmin },
        '/provider/vision/model-training': { title: 'CCI Vision Model Training', component: ProviderWaferIdModelTrainingPage },
        '/provider/site-access': { title: 'Site Access', component: ProviderSiteAccessAdmin },
        '/provider/location-operations': { title: 'Spatial Status', component: ProviderOperationsDashboard },
        '/provider/location-uwb-status': { title: 'Matrix Status', component: UwbDeploymentStatusDashboard },
        '/provider/location-support': { title: 'Support Viewer', component: ProviderSupportViewer },
        '/provider/location-deployment': { title: 'Matrix Deployment', component: UwbDeploymentPage },
        '/provider/efem-setup': { title: 'Axon Setup', component: ProviderEfemSetupPage },
        '/profile': { title: 'User Profile' },
        '/settings': { title: 'User Settings' },
      };
      
      // Try to match the path directly or with leading slash removed
      let route = providerRoutes[cleanPath];

      // Special dynamic route: /provider/devices/:serialNumber (detail page)
      if (!route && /^\/provider\/devices\/[^/]+$/.test(cleanPath) && !cleanPath.endsWith('/inventory')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        route = { title: 'Device Detail', component: DeviceDetail as React.ComponentType<any> };
      }

      // Special dynamic route: /provider/orders/:orderReference (order detail page)
      if (!route && /^\/provider\/orders\/[^/]+$/.test(cleanPath)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        route = { title: 'Order Detail', component: OrderDetail as React.ComponentType<any> };
      }
      
      // Fallback to dashboard
      if (!route) {
        route = providerRoutes['/'];
      }
      
      const RouteComponent = route.component;
      
      return RouteComponent ? (
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        }>
          <RouteComponent />
        </Suspense>
      ) : (
        <PlaceholderComponent title={route.title} currentUserRole={user.role} />
      );
    }

    // Tenant user routes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const routes: { [key: string]: { title: string; component?: React.ComponentType<any> } } = {
      '/': { title: 'Inventory Dashboard', component: InventoryDashboard },
      '/containers': { title: 'Container Management', component: ContainerTracking },
      '/wafers': { title: 'Wafer Management', component: WaferManagement },
      '/wafer-transfer': { title: 'Transfer Progress', component: WaferTransferRedirect },
      '/operations/wafer-slot-update': { title: 'Wafer Slot Update', component: WaferSlotUpdate },
      '/customers': { title: 'Customer Management', component: CustomerManagement },
      '/contacts': { title: 'Contact Management', component: ContactManagement },
      '/operations/conflicts': { title: 'Conflict Resolution', component: ConflictManagement },
      '/operations/audit-runs': { title: 'Audits', component: AuditRunsPage },
      '/operations/temp-files': { title: 'Temp files', component: TempFilesPage },
      '/history/wafers': { title: 'Wafer History', component: HistoryTracking },
      '/history/containers': { title: 'Container History', component: HistoryTracking },
      '/location': { title: 'Node Command Center', component: TagCommandCenter },
      '/location/overview': { title: 'Spatial Overview', component: LocationOverviewPage },
      '/location/tag-fleet': { title: 'Node Fleet Health', component: TagFleetHealthPage },
      '/location/anchor-health': { title: 'Satellite Health', component: SiteAnchorHealthPage },
      '/location/history': { title: 'Node Command Center', component: TagCommandCenter },
      '/location/alarms': { title: 'Node Command Center', component: TagCommandCenter },
      '/location/tags': { title: 'Node Command Center', component: TagCommandCenter },
      '/admin/location/floor-plans': { title: 'Cleanroom Layout', component: FloorPlanAdmin },
      '/users': { title: 'User Management', component: UserManagement },
      '/groups': { title: 'Group Management', component: GroupManagementContainer },
      '/devices/claims': { title: 'Devices', component: DeviceClaims },
      '/devices/efem-setup': { title: 'Axon Setup', component: EfemSetupPage },
      '/devices/scan-templates': { title: 'Scan Templates', component: ScanTemplatesPage },
      '/billing': { title: 'Billing & Subscription', component: BillingPage },
      '/fingerprint': { title: 'Fingerprint Management' },
      '/ocr': { title: 'OCR Results Review' },
      '/efem': { title: 'Axon Integration' },
      '/profile': { title: 'User Profile' },
      '/settings': { title: 'User Settings' },
      '/labels/associate': { title: 'Link Label', component: InventoryLabelAssociateRoute },
      '/labels/link': { title: 'Link Label', component: LabelLinkMobileOnlyRoute },
      '/labels/link/handheld': { title: 'Scan QR', component: LabelLinkMobileOnlyRoute },
      '/operations/jobs': { title: 'Operations Jobs', component: OperationsJobsPage },
      '/operations/jobs/new': { title: 'New Job', component: JobCreationWizardPage },
      '/operations/drafts': { title: 'My Drafts', component: OperationsDraftsPage },
      '/operations/container-create': { title: 'Create Container', component: ContainerCreateWorkflowPage },
      '/operations/wafer-data-refresh': { title: 'Wafer Data Refresh', component: WaferDataRefreshWorkflowPage },
      '/operations/wafer-id-acquire': { title: 'Wafer ID Acquire', component: WaferIdAcquirePage },
      '/operations/audit-workflow': { title: 'Audit Container', component: AuditWorkflowPage },
    };

    const labelScanMatch = cleanPath.match(/^\/l\/([^/]+)\/?$/);
    if (labelScanMatch) {
      const barcodeToken = decodeURIComponent(labelScanMatch[1]);
      return (
        <InventoryLabelLanding barcodeRaw={barcodeToken} />
      );
    }

    if (cleanPath === '/conflicts' || cleanPath.startsWith('/conflicts/')) {
      const prefix = subdomain ? `/${subdomain}` : '';
      const legacyIdMatch = cleanPath.match(/^\/conflicts\/(\d+)$/);
      let qs = typeof window !== 'undefined' ? window.location.search : '';
      if (legacyIdMatch && !qs.includes('conflictId=')) {
        qs = `?conflictId=${legacyIdMatch[1]}`;
      }
      appNavigate(`${prefix}/operations/conflicts${qs}`);
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }

    let route = routes[cleanPath];
    if (
      !route &&
      ((/^\/devices\/[^/]+$/.test(cleanPath) && cleanPath !== '/devices/claims' && cleanPath !== '/devices/efem-setup' && cleanPath !== '/devices/scan-templates') ||
        /^\/devices\/claims\/[^/]+$/.test(cleanPath))
    ) {
      route = { title: 'Device Detail', component: TenantDeviceDetail as React.ComponentType<unknown> };
    }
    if (!route && /^\/devices\/efem-setup\/[^/]+$/.test(cleanPath)) {
      route = { title: 'Axon Setup', component: EfemSetupPage as React.ComponentType<unknown> };
    }
    // Deep-link: /operations/jobs/:jobId/review — slot scan fingerprint review (wizard step 5)
    const scanReviewMatch = cleanPath.match(/^\/operations\/jobs\/([^/]+)\/review$/);
    if (!route && scanReviewMatch) {
      const reviewJobId = decodeURIComponent(scanReviewMatch[1]);
      const ReviewPage = () => <ContainerSlotScanJobReviewPage jobId={reviewJobId} />;
      return (
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        }>
          <ReviewPage />
        </Suspense>
      );
    }
    // Deep-link: /operations/jobs/:jobId — open jobs browser with pre-selected job
    if (!route && /^\/operations\/audit-runs\/[^/]+$/.test(cleanPath)) {
      const runIdSegment = cleanPath.split('/').pop();
      const AuditRunDetail = () => {
        const Detail = React.lazy(() => import('../pages/Operations/AuditRunDetailPage'));
        return (
          <React.Suspense fallback={<CircularProgress />}>
            <Detail runId={runIdSegment} />
          </React.Suspense>
        );
      };
      return (
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        }>
          <AuditRunDetail />
        </Suspense>
      );
    }
    if (!route && isMobileFieldPath(cleanPath)) {
      route = { title: 'Insight Field', component: InsightFieldModeRouter as React.ComponentType<unknown> };
    }
    if (!route && cleanPath.startsWith('/labels/link/')) {
      route = { title: 'Link Label', component: LabelLinkMobileOnlyRoute as React.ComponentType<unknown> };
    }
    if (!route && /^\/operations\/jobs\/[^/]+$/.test(cleanPath) && cleanPath !== '/operations/jobs/new') {
      const jobIdSegment = cleanPath.split('/').pop();
      const JobsWithId = () => <OperationsJobsPage initialJobId={jobIdSegment} />;
      return (
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        }>
          <JobsWithId />
        </Suspense>
      );
    }
    if (!route) {
      route = routes['/'];
    }

    const routeCapabilityKey = resolveRouteCapabilityKey(cleanPath);
    if (routeCapabilityKey && !isRouteEnabled(routeCapabilityKey)) {
      return <RouteEntitlementBlocked routeKey={routeCapabilityKey} />;
    }

    const RouteComponent = route.component;

    const useLocationLayout = LOCATION_LAYOUT_PATHS.has(cleanPath);

    return RouteComponent ? (
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      }>
        {useLocationLayout ? (
          <LocationLayout>
            <RouteComponent currentUserRole={user.role} />
          </LocationLayout>
        ) : (
          <RouteComponent currentUserRole={user.role} />
        )}
      </Suspense>
    ) : (
      <PlaceholderComponent title={route.title} currentUserRole={user.role} />
    );
  }, [user, isProviderUser, cleanPath, isActive, subscriptionLoading, subscriptionError, subdomain, featuresLoading, isRouteEnabled]);

  // Wait for user authentication to complete (early return AFTER all hooks)
  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: (theme) =>
            isMobileFieldBootstrap ? mobileFieldGroupedBg(theme) : theme.palette.background.default,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // User is now guaranteed to exist
  const currentUser = user;

  const isMobileInsightRoute =
    !isProviderUser &&
    (isMobileFieldPath(cleanPath) ||
      (isMobileLayout &&
        cleanPath.startsWith('/labels/link/') &&
        cleanPath !== '/labels/link/handheld'));

  const mobileInventoryRedirectPending =
    !isProviderUser &&
    isMobileLayout &&
    isDesktopInventoryHomePath(cleanPath);

  if (mobileInventoryRedirectPending) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100dvh',
          bgcolor: (theme) => mobileFieldGroupedBg(theme),
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (mobileFieldDesktopEscapePending) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100dvh',
          bgcolor: (theme) => mobileFieldGroupedBg(theme),
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isMobileInsightRoute) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          bgcolor: (theme) => mobileFieldGroupedBg(theme),
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{routeContent}</Box>
        <Snackbar
          open={state.snackbar.open}
          autoHideDuration={6000}
          onClose={() => dispatch({ type: 'HIDE_SNACKBAR' })}
        >
          <Alert
            severity={state.snackbar.severity}
            onClose={() => dispatch({ type: 'HIDE_SNACKBAR' })}
          >
            {state.snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  const shell = (
    <>
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
          <AppShellToolbar
            cleanPath={cleanPath}
            breadcrumbs={appBreadcrumbs}
            drawerOpen={state.drawerOpen}
            drawerWidth={drawerWidth}
            collapsedDrawerWidth={64}
            onDrawerToggle={handleDrawerToggle}
            drawerToggleIcon={state.drawerOpen ? <ChevronLeft /> : <MenuIcon />}
            platformName={appConfig.platformName}
            zIndex={muiTheme.zIndex.drawer + 1}
            themeToggle={
              <AppIconButton onClick={toggleTheme} aria-label="Toggle theme">
                {theme === 'dark' ? <LightMode /> : <DarkMode />}
              </AppIconButton>
            }
            globalActions={
              <>
                <GlobalEfemLockIndicator />
                <AppIconButton onClick={handleNotificationClick} aria-label="Notifications">
                  <Badge
                    badgeContent={unreadCount > 0 ? unreadCount : undefined}
                    color={notificationBadgeColor}
                  >
                    <Notifications />
                  </Badge>
                </AppIconButton>
                <AppIconButton onClick={handleUserMenuOpen} sx={{ ml: 1 }} aria-label="User menu">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={currentUser.avatar}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {personNameInitials(currentUser.firstName, currentUser.lastName)}
                    </Avatar>
                    <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                      {formatPersonName(currentUser.firstName, currentUser.lastName) || currentUser.name}
                    </Typography>
                  </Stack>
                </AppIconButton>
              </>
            }
          />

          {/* Navigation Drawer */}
          <Drawer
            variant="permanent"
            sx={{
              width: state.drawerOpen ? drawerWidth : 64,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: state.drawerOpen ? drawerWidth : 64,
                boxSizing: 'border-box',
                transition: muiTheme.transitions.create('width', {
                  easing: muiTheme.transitions.easing.sharp,
                  duration: muiTheme.transitions.duration.enteringScreen,
                }),
                overflowX: 'hidden',
                borderRight: '1px solid',
                borderRightColor: navChrome.drawerBorder,
                backgroundColor:
                  theme === 'dark' ? navChrome.drawerBgDark : navChrome.drawerBgLight,
                '& .MuiToolbar-root': {
                  backgroundColor: 'transparent',
                },
              },
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                minHeight: { xs: 56, sm: 64 },
                px: 0,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: state.drawerOpen ? 'flex-start' : 'center',
                  px: state.drawerOpen ? drawerOpenPaddingPx / 8 : 0,
                }}
              >
                <BrandLogo
                  variant={state.drawerOpen ? 'full' : 'icon'}
                  height={state.drawerOpen ? 48 : 32}
                  sx={{ ml: state.drawerOpen ? `${fullLogoOpenOffsetPx}px` : 0 }}
                />
              </Box>
            </Toolbar>
            <Box
              sx={{
                overflow: 'auto',
                overflowX: 'hidden',
                pt: 0.5,
                pb: 2,
              }}
            >
              <List sx={{ px: 0 }}>
                {navigationItems.map(item => renderNavigationItem(item))}
              </List>
            </Box>
          </Drawer>

          {/* Main Content */}
          <Box 
            component="main" 
            sx={{ 
              flexGrow: 1, 
              width: `calc(100% - ${state.drawerOpen ? drawerWidth : 64}px)`,
              display: 'flex',
              flexDirection: 'column',
              height: '100vh',
              overflow: 'hidden',
              transition: muiTheme.transitions.create(['width', 'margin'], {
                easing: muiTheme.transitions.easing.sharp,
                duration: muiTheme.transitions.duration.leavingScreen,
              }),
            }}
          >
            <AppToolbarOffsetSpacer />
            {/* Scrollable content area: grows to fill remaining height; column flex so route pages can use flex:1 for full-height tools */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {/* Optimized route-based content rendering with lazy loading */}
                {routeContent}
              </Box>
            </Box>
          </Box>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <Stack>
                <Typography variant="subtitle2">{currentUser.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {currentUser.email} • {currentUser.role}
                </Typography>
              </Stack>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleProfileClick}>
              <ListItemIcon><AccountCircle /></ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleSettingsClick}>
              <ListItemIcon><Settings /></ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { width: 350, maxHeight: 400 } }}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2">
                Notifications{unreadCount > 0 ? ` (${unreadCount})` : ''}
              </Typography>
            </MenuItem>
            <Divider />
            {notifications.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  No notifications
                </Typography>
              </MenuItem>
            ) : (
              notifications.slice(0, 10).map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => markAsRead(notification.id)}
                  sx={{ position: 'relative', alignItems: 'flex-start', py: 1.5 }}
                >
                  <Stack spacing={0.75} sx={{ width: '100%', pr: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Typography variant="body2" fontWeight={notification.read ? 400 : 600}>
                        {notification.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {formatTime(notification.timestamp)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {notification.message}
                    </Typography>
                    {notification.actions && notification.actions.length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {notification.actions.map((action) => (
                          <AppButton
                            key={action.label}
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              action.action();
                              markAsRead(notification.id);
                              handleNotificationClose();
                            }}
                          >
                            {action.label}
                          </AppButton>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                  <AppIconButton
                    size="small"
                    aria-label="Dismiss notification"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDismissNotification(notification.id);
                      handleNotificationClose();
                    }}
                    sx={{ position: 'absolute', right: 4, top: 8 }}
                  >
                    <Close fontSize="small" />
                  </AppIconButton>
                </MenuItem>
              ))
            )}
          </Menu>

          {/* Popup Menu for Collapsed Drawer - Shows submenu items */}
          <Popover
            open={Boolean(popupMenuAnchor)}
            anchorEl={popupMenuAnchor}
            onClose={handlePopupMenuLeave}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            disableRestoreFocus
            sx={{
              pointerEvents: 'none',
            }}
            PaperProps={{
              sx: {
                pointerEvents: 'auto',
                ml: 0.5,
                minWidth: 200,
                boxShadow: navChrome.popoverShadow,
              },
              onMouseEnter: handlePopupMenuEnter,
              onMouseLeave: handlePopupMenuLeave,
            }}
          >
            {popupMenuItem?.children && (
              <List sx={{ py: 1 }}>
                {popupMenuItem.children
                  .filter(child => hasPermission(child))
                  .map((child) => {
                    const isActive = !!(child.path && cleanPath === child.path);
                    return (
                      <ListItem key={child.id} disablePadding>
                        <ListItemButton
                          selected={isActive}
                          onClick={() => {
                            if (child.path) {
                              const fullPath = subdomain ? `/${subdomain}${child.path}` : child.path;
                              window.history.pushState({}, '', fullPath);
                              setCurrentPath(fullPath);
                              handlePopupMenuClose();
                            }
                          }}
                          sx={{
                            py: 1,
                            px: 2,
                            '&.Mui-selected': {
                              backgroundColor: navChrome.popoverSelectedBg,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: isActive ? 'primary.main' : 'inherit' }}>
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: isActive ? 600 : 400,
                              color: isActive ? 'primary.main' : 'inherit',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
              </List>
            )}
          </Popover>

          {/* Snackbar for notifications */}
          <Snackbar
            open={state.snackbar.open}
            autoHideDuration={6000}
            onClose={() => dispatch({ type: 'HIDE_SNACKBAR' })}
          >
            <Alert 
              severity={state.snackbar.severity} 
              onClose={() => dispatch({ type: 'HIDE_SNACKBAR' })}
            >
              {state.snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
    </>
  );

  return isLocationLayoutPath(cleanPath) ? (
    <LocationProvider>{shell}</LocationProvider>
  ) : (
    shell
  );
});

InsightAppInner.displayName = 'InsightAppInner';

/**
 * Outer component that wraps with FormattingProvider and TenantFeaturesProvider
 */
export const InsightApp: React.FC = () => {
  return (
    <FormattingProvider>
      <TenantFeaturesProvider>
        <AppToolbarProvider>
          <InsightAppInner />
        </AppToolbarProvider>
      </TenantFeaturesProvider>
    </FormattingProvider>
  );
};

InsightApp.displayName = 'InsightApp';